/* eslint-disable no-plusplus */
/* eslint-disable unicorn/consistent-function-scoping */
/* eslint-disable unicorn/no-array-push-push */
import { Dispatch } from 'redux';
import { AxiosResponse } from 'axios';
import { Result, searchActionNames } from './types';
import UserPreferences from '../../services/user-preferences-v2';
import Devices from '../../services/devices';
import { AppState, AppThunkDispatch } from '..';
import { DeviceWithAssetAndRadioData } from '../../entities/device-radios-v2/device-with-asset-and-radio-data-response';

import { devicesActionNames } from '../devices/types';
import { UserPreferenceNames } from '../user-preferences-v2/types';
import {
  createResultFromAssetForEcmDetails,
  createResultFromDevicelessAsset,
  createResultFromResponseForAssetFromMaster,
  createResultFromResponseForAssetFromRadio,
  createResultFromResponseForDevice,
  createResultFromResponseForDeviceFromRadio,
} from './search-result-mappings';
import { setModalOpen } from '../modal/actions';
import Assets from '../../services/assets-v2';
import { AssetsResponse } from '../../entities/assets-v2';
import VimsSFTPUser from '../../services/vims-sftp-users';

const suspendedMessage =
  'Device Suspended/Disabled, all features except Device Configuration will be enabled';

export const updateRecentFlag = (flag: boolean) => (dispatch: Dispatch) => {
  dispatch({
    type: searchActionNames.SET_UPDATE_RECENT_FLAG,
    payload: flag,
  });
};

export const saveRecentSearches =
  (input: string) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
      search: { recent },
      userPreference: { customUserPreferences },
    } = getState();
    const dupRemovedResults = recent.filter((recentInstance) => recentInstance.value !== input);
    const sortedResults = dupRemovedResults.sort((a, b) => b.timeStamp - a.timeStamp);
    if (sortedResults.length > 14) sortedResults.length = 14;
    const inputWithTime = {
      value: input,
      isFavourite: false,
      timeStamp: Date.now(),
    };
    const service = new UserPreferences(client, envVariables.userPreferencesV2BaseUrl);
    const recentlyViewedSearches = JSON.stringify([...sortedResults, inputWithTime]);
    await service.modifyCustomUserPreferences({
      ...customUserPreferences,
      recentlyViewedSearches,
    });
    dispatch({
      type: UserPreferenceNames.SET_RECENT_USER_SEARCHES,
      payload: recentlyViewedSearches,
    });
    dispatch(updateRecentFlag(true));
  };

export const getRecentSearches =
  () => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const service = new UserPreferences(client, envVariables.userPreferencesV2BaseUrl);
    const response: any = await service.getCustomUserPreferences();
    if (response.data.recentlyViewedSearches) {
      const searchesObject = JSON.parse(response.data.recentlyViewedSearches);
      dispatch({
        type: searchActionNames.SET_RECENT_SEARCHES,
        payload: searchesObject,
      });
    }
    dispatch(updateRecentFlag(false));
  };

export const markFavouriteSearches =
  (jsonObject: any) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
      search: { recent },
      userPreference: { customUserPreferences },
    } = getState();
    const dupRemovedResults = recent.filter((recentInstance) => recentInstance.value !== jsonObject.value);
    let sortedResults = dupRemovedResults.sort((a, b) => b.timeStamp - a.timeStamp);
    if (sortedResults.length > 14) {
      try {
        sortedResults = sortedResults.filter((item) => item.isFavourite === true);
        sortedResults = sortedResults.sort((a, b) => b.timeStamp - a.timeStamp);
      } catch (error) {
        console.log(error);
      }
    }
    const inputWithTime = {
      value: jsonObject.value,
      timeStamp: Date.now(),
      isFavourite: !jsonObject.isFavourite as boolean,
    };
    const service = new UserPreferences(client, envVariables.userPreferencesV2BaseUrl);
    const recentlyViewedSearches = JSON.stringify([...sortedResults, inputWithTime]);
    await service.modifyCustomUserPreferences({
      ...customUserPreferences,
      recentlyViewedSearches,
    });
    dispatch({
      type: UserPreferenceNames.SET_RECENT_USER_SEARCHES,
      payload: recentlyViewedSearches,
    });
    dispatch({
      type: searchActionNames.SET_RECENT_SEARCHES,
      payload: JSON.parse(recentlyViewedSearches),
    });
    dispatch(updateRecentFlag(true));
  };

const dedupe = (payloads: Result[]) =>
  payloads.filter(
    (payload: Result, index: number, existing: any) =>
      existing.findIndex(
        (self: any) =>
          self.deviceSerialNumber === payload.deviceSerialNumber &&
          self.deviceType === payload.deviceType,
      ) === index,
  );

export const getResult =
  (input: string) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const promises: Promise<AxiosResponse<DeviceWithAssetAndRadioData | AssetsResponse[]>>[] = [];
    const bulkDeviceReturnPromises: Promise<AxiosResponse<DeviceWithAssetAndRadioData[]>>[] = [];

    dispatch({ type: searchActionNames.SET_HAS_ERROR_FLAG, payload: false });
    dispatch({ type: searchActionNames.SET_DEVICE_INFO_ERROR, payload: false });
    dispatch({ type: searchActionNames.SET_DEVICE_FORBIDDEN_ERROR, payload: false });
    dispatch({ type: searchActionNames.SET_ECM_DATA, payload: [] });
    const {
      login: { client, envVariables },
    } = getState();
    const trimmedInput = input.trim();
    const devices = new Devices(client, envVariables.deviceApiV2BaseUrl);
    const assets = new Assets(client, envVariables.assetsV2BaseUrl);
    const sftpUsers = new VimsSFTPUser(client, envVariables.vimsSFTPUsers);

    bulkDeviceReturnPromises.push(devices.bulkGetByDeviceSerialNumber(trimmedInput));
    bulkDeviceReturnPromises.push(devices.bulkGetByDeviceId({ deviceId: trimmedInput }));
    bulkDeviceReturnPromises.push(devices.bulkGetByDeviceKey(trimmedInput));

    promises.push(devices.getByKey({ deviceKey: trimmedInput }));
    promises.push(devices.getByIdAndType({ id: trimmedInput, assetEnrichment: true }));
    promises.push(devices.getByRadioSerialNumber(trimmedInput));
    promises.push(devices.getByDeviceSerialNumber(trimmedInput));
    promises.push(assets.getByTelematicsAssetId(trimmedInput));
    promises.push(assets.getByAssetSerialNumber(trimmedInput));
    promises.push(sftpUsers.getDealer(trimmedInput));

    const successfulEntityReturn: DeviceWithAssetAndRadioData[] = [];

    const responses = await Promise.allSettled(promises);

    const bulkDeviceEndpointResponses = await Promise.allSettled(bulkDeviceReturnPromises);
    bulkDeviceEndpointResponses
      .filter(
        (response: PromiseSettledResult<AxiosResponse<DeviceWithAssetAndRadioData[]>>) =>
          response.status === 'fulfilled' &&
          response.value &&
          response.value.data.every((device) => device.status !== 404),
      )
      .forEach((responseSuccessful: any) =>
        responseSuccessful.value?.data.forEach((device: any) =>
          successfulEntityReturn.push(device),
        ),
      );

    responses
      .filter(
        (
          response: PromiseSettledResult<
            AxiosResponse<DeviceWithAssetAndRadioData | AssetsResponse[]>
          >,
        ) => response.status === 'fulfilled',
      )
      .forEach((responseSuccessful: any) =>
        successfulEntityReturn.push(responseSuccessful.value.data),
      );

    // Error handling for 403, 404, 500, 504, network errors
    const errorList: any[] = [];
    let hasInternalServerError = false;
    let hasGatewayTimeoutError = false;
    let hasNetworkError = false;
    let error404Count = 0;
    let error500Count = 0;
    let error504Count = 0;
  
    bulkDeviceEndpointResponses.forEach((response: any) => {
      if (response.status === 'rejected') {
        const status = response?.reason?.response?.status;
        if (status === 403) errorList.push(status);
        if (status === 404) error404Count++;
        if (status === 500) error500Count++;
        if (status === 504) error504Count++;
        if (!status) hasNetworkError = true;
      }
    });

    responses.forEach((response: any) => {
      if (response.status === 'rejected') {
        const status = response?.reason?.response?.status;
        if (status === 403) errorList.push(status);
        if (status === 404) error404Count++;  
        if (status === 500) error500Count++;
        if (status === 504) error504Count++;
        if (!status) hasNetworkError = true;
      }
    });

    if (error500Count >= 7) {
      hasInternalServerError = true;
    }
    if (error504Count >= 7) {
      hasGatewayTimeoutError = true;
    }

     if (errorList.length >= 10) {
      dispatch({ type: searchActionNames.SET_DEVICE_FORBIDDEN_ERROR, payload: true });
    } else if (hasInternalServerError) {
      dispatch({ type: searchActionNames.SET_INTERNAL_SERVER_ERROR, payload: true });
    } else if (hasGatewayTimeoutError) {
      dispatch({ type: searchActionNames.SET_GATEWAY_TIMEOUT_ERROR, payload: true });
    } else if (error404Count >= 7) {
      dispatch({ type: searchActionNames.SET_DEVICE_NOT_FOUND_ERROR, payload: true });
    } else if (hasNetworkError) {
      dispatch({ type: searchActionNames.SET_NETWORK_FAILURE_ERROR, payload: true });
    }
 
    if (successfulEntityReturn.length > 0) {
      const preliminaryPayloadList: any = [];
      const ecmDataList:any=[];
      successfulEntityReturn.forEach((successfulEntities: any) => {
        if ((successfulEntities as DeviceWithAssetAndRadioData).deviceStatus === 'disabled') {
          dispatch({
            type: devicesActionNames.SET_IS_SUSPENDED_DEVICE,
            payload: true,
          });
          dispatch(
            setModalOpen({
              open: true,
              message: suspendedMessage,
              accept: () => { },
              acceptButtonText: 'ok',
            }),
          );
        }
        if ((successfulEntities as AssetsResponse[])[0]?.assetMake) {
          successfulEntities.forEach((successfulEntity: AssetsResponse) => {
            if (successfulEntity.devices.length !== 0)
              successfulEntity.devices.forEach((device: any, indexDevice: number) => {
                device.radios.forEach((radio: any, indexRadio: number) => {
                  if (radio.isTethered)
                    preliminaryPayloadList.push(
                      createResultFromResponseForAssetFromRadio(successfulEntity, indexRadio),
                    );
                });
                preliminaryPayloadList.push(
                  createResultFromResponseForAssetFromMaster(successfulEntity, indexDevice),
                );
              });
              if(successfulEntity.ecms && successfulEntity.ecms.length !== 0){
                successfulEntity.ecms.forEach((ecm:any,indexEcm:number)=>{
                  ecmDataList.push(createResultFromAssetForEcmDetails(successfulEntity,indexEcm),);
                });
              }
          });
        } else if (successfulEntities.device) {
          successfulEntities.device?.radios?.forEach((radio: any, indexDevice: number) => {
            if (radio.isTethered)
              preliminaryPayloadList.push(
                createResultFromResponseForDeviceFromRadio(successfulEntities, indexDevice),
              );
          });
          preliminaryPayloadList.push(createResultFromResponseForDevice(successfulEntities));
        } else if (successfulEntities.devices) {
          successfulEntities.devices.forEach((device: any, indexDevice: number) => {
            preliminaryPayloadList.push(
              createResultFromResponseForAssetFromMaster(successfulEntities, indexDevice),
            );
          });
        }
        if (successfulEntities.vimsSftpUsers) {
          const dealer = successfulEntities.vimsSftpUsers[0];
          preliminaryPayloadList.push({
            deviceId: dealer.vimsSftpUserId,
            assetMake: dealer.vimsSftpUserKey,
            moduleId: ' ',
            deviceKey: dealer.vimsSftpUserId,
            dealer: true,
            dealerCode: dealer.vimsSftpUserKey,
          });
          localStorage.setItem('vimsSftpUserId', dealer.vimsSftpUserId);
        }
      });
      localStorage.removeItem('transaction-table-sort-by');
      dispatch(saveRecentSearches(input));
      const payloadList = dedupe(preliminaryPayloadList).filter((device: any) => {
        const deviceAttriuteNames = Object.keys(device);
        let validDevice = false;
        deviceAttriuteNames.forEach((attribute: string) => {
          if (typeof device[attribute] === 'string' && device[attribute] === input) {
            validDevice = true;
          }
        });
        return validDevice;
      });
      if (payloadList.length) {
        payloadList.forEach((payload: any) => {
          dispatch({
            type: searchActionNames.ADD_RESULT,
            payload,
          });
        });
      } else {
        dispatch({
          type: searchActionNames.SET_HAS_ERROR_FLAG,
          payload: true,
        });
      }
      if(ecmDataList.length){
        dispatch({
          type: searchActionNames.SET_ECM_DATA,
          payload: ecmDataList,
        });
      }
    } else {
      dispatch({
        type: searchActionNames.SET_HAS_ERROR_FLAG,
        payload: true,
      });
    }
  };

export const getVIMSSearchResult =
  (input: string) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const promises: Promise<AxiosResponse<DeviceWithAssetAndRadioData | AssetsResponse[]>>[] = [];
    const {
      login: { client, envVariables },
    } = getState();
    const trimmedInput = input.trim();
    const assets = new Assets(client, envVariables.assetsV2BaseUrl);
    promises.push(assets.getByAssetSerialNumber(trimmedInput));
    promises.push(assets.getByTelematicsAssetId(trimmedInput));
    const successfulEntityReturn: DeviceWithAssetAndRadioData[] = [];
    const responses = await Promise.allSettled(promises);
    const [serialNumberResult, telematicsIdResult] = responses;
    const isSerialNumberSuccess = serialNumberResult.status === 'fulfilled';
    const isTelematicsIdFailure = telematicsIdResult.status === 'rejected';
    if (isSerialNumberSuccess && isTelematicsIdFailure) {
      dispatch({
        type: searchActionNames.SET_TELEMATICS_ID_ERROR,
        payload: true,
      });
    }
    responses
      .filter(
        (
          response: PromiseSettledResult<
            AxiosResponse<DeviceWithAssetAndRadioData | AssetsResponse[]>
          >,
        ) => response.status === 'fulfilled',
      )
      .forEach((responseSuccessful: any) =>
        successfulEntityReturn.push(responseSuccessful.value.data),
      );

    // Error handling for 403, 404, 500, 504, network errors
    const errorList: any[] = [];
    let hasInternalServerError = false;
    let hasGatewayTimeoutError = false;
    let hasNetworkError = false;

    responses.forEach((response: any) => {
      if (response.status === 'rejected') {
        const status = response?.reason?.response?.status;
        if (status === 403) errorList.push(status);
        if (status === 404) dispatch({ type: searchActionNames.SET_DEVICE_NOT_FOUND_ERROR, payload: true });
        if (status === 500) hasInternalServerError = true;
        if (status === 504) hasGatewayTimeoutError = true;
        if (!status) hasNetworkError = true;
      }
    });

    if (errorList.length >= 2) {
      dispatch({ type: searchActionNames.SET_DEVICE_FORBIDDEN_ERROR, payload: true });
    }
    if (hasInternalServerError) {
      dispatch({ type: searchActionNames.SET_INTERNAL_SERVER_ERROR, payload: true });
    }
    if (hasGatewayTimeoutError) {
      dispatch({ type: searchActionNames.SET_GATEWAY_TIMEOUT_ERROR, payload: true });
    }
    if (hasNetworkError) {
      dispatch({ type: searchActionNames.SET_NETWORK_FAILURE_ERROR, payload: true });
    }

    if (successfulEntityReturn.length > 0) {
      const preliminaryPayloadList: any = [];
      successfulEntityReturn.forEach((successfulEntities: any) => {
        if ((successfulEntities as DeviceWithAssetAndRadioData).deviceStatus === 'disabled') {

          dispatch({
            type: devicesActionNames.SET_IS_SUSPENDED_DEVICE,
            payload: true,
          });
          dispatch(
            setModalOpen({
              open: true,
              message: suspendedMessage,
              accept: () => { },
              acceptButtonText: 'ok',
            }),
          );
        }
        if (Array.isArray(successfulEntities) && successfulEntities.length > 0 && successfulEntities[0].devices.length === 0) {
          successfulEntities.forEach((successfulEntity: AssetsResponse) => {
            preliminaryPayloadList.push(
              createResultFromDevicelessAsset(successfulEntity),
            );
          });
        }
      });
      localStorage.removeItem('vims-transaction-table-sort-by');
      dispatch(saveRecentSearches(input));
      const payloadList = dedupe(preliminaryPayloadList).filter((device: any) => {
        const deviceAttriuteNames = Object.keys(device);
        let validDevice = false;
        deviceAttriuteNames.forEach((attribute: string) => {
          if (typeof device[attribute] === 'string' && device[attribute] === input) {
            validDevice = true;
          }
        });
        return validDevice;
      });
      if (payloadList.length > 0) {
        payloadList.forEach((payload: any) => {
          dispatch({
            type: searchActionNames.ADD_RESULT,
            payload,
          });
        });
      } else {
        dispatch({
          type: searchActionNames.SET_HAS_ERROR_FLAG,
          payload: true,
        });
      }
    } else {
      dispatch({
        type: searchActionNames.SET_HAS_ERROR_FLAG,
        payload: true,
      });
    }
  };

export const setSelectedEntityByDeviceId =
  (deviceId: string) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    dispatch({ type: searchActionNames.SET_HAS_ERROR_FLAG, payload: false });
    dispatch({ type: searchActionNames.SET_DEVICE_INFO_ERROR, payload: false });
    dispatch({ type: searchActionNames.SET_DEVICE_FORBIDDEN_ERROR, payload: false });
    const {
      login: { client, envVariables },
    } = getState();
    try {
      const devices = new Devices(client, envVariables.deviceApiV2BaseUrl);
      const selectedDevice = await devices.getByIdAndType({ id: deviceId, assetEnrichment: true });
      dispatch({
        type: searchActionNames.SET_SELECTED_ENTITY,
        payload: createResultFromResponseForDevice(selectedDevice.data),
      });
      if (selectedDevice.data.device) {
        dispatch({
          type: searchActionNames.SET_OTASP_STATUS,
          payload: selectedDevice.data.device.otaspStatus,
        });
      }
      return true;
    } catch {
      return false;
    }
  };

export const setSelectedEntityTelematicsId =
  (telematicsAssetId: string) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    try {
      const assets = new Assets(client, envVariables.assetsV2BaseUrl);
      const response: AxiosResponse<any> = await assets.getByTelematicsAssetId(telematicsAssetId);

      const selectedAsset = response.data;
      const payload =
        selectedAsset.devices.length > 0
          ? createResultFromResponseForAssetFromMaster(selectedAsset, 0)
          : createResultFromDevicelessAsset(selectedAsset);
      dispatch({
        type: searchActionNames.SET_SELECTED_VIMS_ENTITY,
        payload,
      });
      return true;
    } catch {
      return false;
    }
  };

export const resetErrorFlag = () => (dispatch: Dispatch) => {
  dispatch({ type: searchActionNames.SET_HAS_ERROR_FLAG, payload: false });
  dispatch({ type: searchActionNames.SET_DEVICE_INFO_ERROR, payload: false });
  dispatch({ type: searchActionNames.SET_DEVICE_FORBIDDEN_ERROR, payload: false });
  dispatch({ type: searchActionNames.SET_DEVICE_NOT_FOUND_ERROR, payload: false });
  dispatch({ type: searchActionNames.SET_TELEMATICS_ID_ERROR, payload: false });
};

export const clearResults = () => async (dispatch: Dispatch) => {
  dispatch({ type: searchActionNames.SET_HAS_ERROR_FLAG, payload: false });
  dispatch({ type: searchActionNames.SET_DEVICE_INFO_ERROR, payload: false });
  dispatch({ type: searchActionNames.SET_DEVICE_FORBIDDEN_ERROR, payload: false });
  dispatch({ type: searchActionNames.SET_TELEMATICS_ID_ERROR, payload: false });
  dispatch({ type: searchActionNames.SET_DEVICE_NOT_FOUND_ERROR, payload: false });
  dispatch({ type: searchActionNames.CLEAR_RESULT });
};

export const setSelectedEntityFromDeviceOrTelematicsId =
  (id: string) => async (dispatch: AppThunkDispatch) => {
    dispatch(resetErrorFlag());
    const setByDeviceId = await dispatch(setSelectedEntityByDeviceId(id));
    if (!setByDeviceId) dispatch(setSelectedEntityTelematicsId(id));
  };
